import { faMagnifyingGlassMinus, faMagnifyingGlassPlus } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { LinearProgress, Typography, alpha } from "@mui/material"
import { appEventModule } from "app/event"
import { translate } from "app/language/service"
import { CREATIVE_IMAGE_DOM_ID, ICreativeFileType, MEDIA_ERROR_URL } from "media/model"
import React from "react"
import ReactPlayer from "react-player"
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch"
import { FlexBox } from "ui/components/FlexBox"

interface IMediaDisplayProps {
  parentDomId: string
  type: ICreativeFileType
  mediaUrl: string
  sx?: any
  isFullScreen?: boolean
  videoHeight?: string
}

export const MediaDisplay = React.memo(
  ({ parentDomId, type, mediaUrl, sx, isFullScreen, videoHeight }: IMediaDisplayProps) => {
    const [isLoadingImage, setIsLoadingImage] = React.useState(type === "image")
    const [hasImageError, setHasImageError] = React.useState(false)
    const [hasVideoError, setHasVideoError] = React.useState(false)
    const photoResizeTimeOut = React.useRef<any>(undefined)
    const resizeImageOrVideo = () => {
      clearTimeout(photoResizeTimeOut.current)
      photoResizeTimeOut.current = setTimeout(() => {
        const parent = document.getElementById(parentDomId)
        const parentHeight = parent?.offsetHeight || 0
        const screenMaxWidth = Math.max(isFullScreen ? document.body.offsetWidth - 100 : parent?.offsetWidth || 0, 100)
        let screenMaxHeight = Math.max(
          isFullScreen ? document.body.offsetHeight - parentHeight - 100 : parentHeight - 16,
          100
        )
        if (type === "image") {
          const img = document.getElementById(CREATIVE_IMAGE_DOM_ID)
          if (!img) {
            return
          }
          img.style.maxWidth = "" // don't remove !! necessary to calculate width
          img.style.maxWidth = `${screenMaxWidth}px`
          img.style.maxHeight = "" // don't remove !! necessary to calculate height
          img.style.maxHeight = `${screenMaxHeight}px`
        } else if (type === "video") {
          const elements = document.getElementsByClassName("react-player")
          if (elements.length === 0) {
            return
          }
          const player = elements[0] as HTMLElement
          player.style.maxWidth = "" // don't remove !! necessary to calculate width
          player.style.maxWidth = `${screenMaxWidth}px`
          player.style.maxHeight = "" // don't remove !! necessary to calculate height
          player.style.maxHeight = `${screenMaxHeight}px`
          player.style.height = "" // don't remove !! necessary to calculate height
        }
      }, 250)
    }
    React.useEffect(() => {
      appEventModule.on.windowResize(`MediaDisplay-${parentDomId}`, () => resizeImageOrVideo())
      return () => appEventModule.remove(`MediaDisplay-${parentDomId}`)
    })
    return (
      <FlexBox
        ai="start"
        vertical
        fullWidth
        sx={{
          "position": "relative",
          "& .image": {
            width: "100%"
          },
          ...sx
        }}>
        {hasVideoError ? (
          <Typography id="media-display-video-error" color="error" fontSize=".9rem">
            {translate("campaign.creative.video.error")}
          </Typography>
        ) : hasImageError ? (
          <Typography id="media-display-image-error" color="error" fontSize=".9rem">
            {translate("campaign.creative.image.error")}
          </Typography>
        ) : mediaUrl === MEDIA_ERROR_URL ? (
          <Typography id="media-display-file-missing" color="error" fontSize=".9rem">
            {translate("media.missing.file")}
          </Typography>
        ) : type === "image" ? (
          <FlexBox vertical fullWidth>
            <TransformWrapper>
              {({ zoomIn, zoomOut, resetTransform }) => (
                <FlexBox fullWidth vertical sx={{ position: "relative" }}>
                  {!isLoadingImage && (
                    <FlexBox
                      gap
                      jc="flex-end"
                      ai="flex-start"
                      sx={{
                        "position": "absolute",
                        "zIndex": 1,
                        "top": ".5rem",
                        "right": ".5rem",
                        "p": "1rem",
                        "pt": ".4rem",
                        "pb": ".4rem",
                        "backgroundColor": theme => alpha(theme.palette.background.default, 0.6),
                        "& .icon": {
                          color: theme => theme.palette.text.primary2,
                          fontSize: "1.4rem",
                          cursor: "pointer"
                        }
                      }}>
                      <FontAwesomeIcon icon={faMagnifyingGlassMinus} className="icon" onClick={() => zoomOut()} />
                      <FontAwesomeIcon icon={faMagnifyingGlassPlus} className="icon" onClick={() => zoomIn()} />
                    </FlexBox>
                  )}
                  <TransformComponent>
                    <FlexBox
                      fullWidth
                      sx={{
                        "position": "relative",
                        "& > p.button": {
                          position: "absolute",
                          top: ".5rem",
                          left: ".5rem",
                          backgroundColor: theme => theme.palette.background.default,
                          width: "1.5rem",
                          height: "1.5rem",
                          lineHeight: "1.5rem",
                          textAlign: "center",
                          verticalAlign: "middle",
                          zIndex: 1,
                          cursor: "pointer",
                          fontSize: "1.2rem",
                          borderRadius: ".25rem"
                        },
                        "& > p.button:hover": {
                          backgroundColor: theme => theme.palette.background.paper
                        },
                        "& img": {
                          borderRadius: ".1875rem",
                          maxWidth: "95%",
                          maxHeight: "90%",
                          ml: "1rem",
                          mr: "1rem",
                          mb: "1rem"
                        },
                        "& img.loading": {
                          filter: "grayscale(1)"
                        }
                      }}>
                      <img
                        id={CREATIVE_IMAGE_DOM_ID}
                        className={isLoadingImage ? "loading" : undefined}
                        src={mediaUrl}
                        onLoad={() => {
                          resetTransform()
                          setIsLoadingImage(false)
                          resizeImageOrVideo()
                        }}
                        onError={() => {
                          setIsLoadingImage(false)
                          setHasImageError(true)
                        }}
                      />
                    </FlexBox>
                  </TransformComponent>
                </FlexBox>
              )}
            </TransformWrapper>
            {isLoadingImage && <LinearProgress sx={{ width: "100%" }} />}
          </FlexBox>
        ) : type === "video" ? (
          <FlexBox vertical fullWidth fullHeight>
            <ReactPlayer
              className="react-player"
              url={mediaUrl}
              controls
              height={videoHeight}
              onError={() => setHasVideoError(true)}
              onReady={() => resizeImageOrVideo()}
            />
          </FlexBox>
        ) : (
          <Typography>Media format not supported</Typography>
        )}
      </FlexBox>
    )
  }
)
